import { fetchWithRefresh } from "../../api/FetchWithRefresh";
import { useState, useEffect, useRef, useCallback } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import LinearProgress from "@mui/material/LinearProgress";
import Alert from "@mui/material/Alert";
import TablePagination from "@mui/material/TablePagination";
import Menu from "@mui/material/Menu";
import { MenuActionButton } from "../../ui/MenuActionButton";
import { useNavigate } from "react-router-dom";
import { useStatisticList } from "./useStatisticList";
import type { StatisticListItem } from "./useStatisticList";
import { StatisticListFilters } from "./StatisticListFilters";
import { StatisticDataGrid } from "./StatisticDataGrid";
import { IfPermission } from "../../auth/IfPermission";
import type { paths } from "../../api/generated/api-types";
import type { JSX } from "react";

type RegionsResponse =
  paths["/sites/regions"]["get"]["responses"]["200"]["content"]["application/json"];

export function StatisticList() {
  let statisticList: JSX.Element;
  const navigate = useNavigate();
  const {
    state,
    fetching,
    page,
    pageSize,
    exportState,
    setFilters,
    setPage,
    setPageSize,
    refetch,
    exportCsv,
  } = useStatisticList();
  const [regions, setRegions] = useState<string[]>([]);
  const [exportAnchorEl, setExportAnchorEl] = useState<HTMLElement | null>(null);
  const regionsLoaded = useRef(false);
  useEffect(() => {
    if (regionsLoaded.current) {
      return;
    }
    regionsLoaded.current = true;
    fetchWithRefresh("/api/sites/regions?credentialType=article")
      .then(async (response) => {
        if (response.ok) {
          const data = (await response.json()) as RegionsResponse;
          setRegions(data.regions.map((row) => row.region));
        }
      })
      .catch(() => {
        // regions filter will be empty
      });
  }, []);
  const handleRowClick = useCallback(
    (item: StatisticListItem) => {
      const parameters = new URLSearchParams();
      parameters.set("targetUrl", item.targetUrl);
      parameters.set("language", item.language);
      parameters.set("region", item.region);
      void navigate(`/statistics/detail?${parameters.toString()}`);
    },
    [navigate],
  );
  const handleExportClick = (event: React.MouseEvent<HTMLElement>) => {
    setExportAnchorEl(event.currentTarget);
  };
  const handleExportClose = () => {
    setExportAnchorEl(null);
  };
  const handleExportVariant = (variant: "summary" | "detailed") => {
    setExportAnchorEl(null);
    exportCsv(variant);
  };
  // MUI считает страницы с нуля, экран — с единицы.
  const zeroBasedPage = page - 1;

  if (state.kind === "loading") {
    statisticList = (
      <Box p={3}>
        <Typography variant="h4" mb={2}>Статистика</Typography>
        <Box mb={2}>
          <StatisticListFilters onChange={setFilters} regions={regions} />
        </Box>
        <Box display="flex" justifyContent="center" p={6}>
          <CircularProgress />
        </Box>
      </Box>
    );
  } else {
    if (state.kind === "error") {
      statisticList = (
      <Box p={3}>
        <Typography variant="h4" mb={2}>Статистика</Typography>
        <Box mb={2}>
          <StatisticListFilters onChange={setFilters} regions={regions} />
        </Box>
        <Alert
          severity="error"
          action={<Button size="small" onClick={refetch}>Повторить</Button>}
        >
          Ошибка загрузки: {state.message}
        </Alert>
      </Box>
    );
    } else {
      statisticList = (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">Статистика</Typography>
        <IfPermission permission="statistics.export">
          <Box display="flex" gap={1} alignItems="center">
            <Button
              variant="outlined"
              onClick={handleExportClick}
              disabled={exportState.kind === "loading"}
            >
              {exportState.kind === "loading" ? "Экспорт..." : "Экспорт CSV"}
            </Button>
            <Menu
              anchorEl={exportAnchorEl}
              open={exportAnchorEl !== null}
              onClose={handleExportClose}
            >
              <MenuActionButton onClick={() => handleExportVariant("summary")}>
                Без деталей
              </MenuActionButton>
              <MenuActionButton onClick={() => handleExportVariant("detailed")}>
                С деталями
              </MenuActionButton>
            </Menu>
          </Box>
        </IfPermission>
      </Box>

      <Box mb={2}>
        <StatisticListFilters onChange={setFilters} regions={regions} />
      </Box>

      {fetching && <LinearProgress sx={{ mb: 1 }} />}

      {exportState.kind === "error" && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => {}}>
          Ошибка экспорта: {exportState.message}
        </Alert>
      )}

      {state.kind === "empty" ? (
        <Box textAlign="center" py={4}>
          <Typography color="text.secondary">Нет данных, соответствующих фильтрам</Typography>
        </Box>
      ) : (
        <>
          <StatisticDataGrid
            items={state.items}
            onRowClick={handleRowClick}
          />
          <TablePagination
            component="div"
            count={state.totalCount}
            page={zeroBasedPage}
            onPageChange={(_event, newPage) => setPage(newPage + 1)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(event) => setPageSize(Number(event.target.value))}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage="На странице:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}–${to} из ${count}`
            }
          />
        </>
      )}
    </Box>
  );
    }
  }
  return statisticList;
}
